/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
export type AdminCatalogImportInput = {
  characters?: Array<AdminCatalogImportRowInput> | null | undefined;
  conflicts?: Array<AdminCatalogImportRowInput> | null | undefined;
  morals?: Array<AdminCatalogImportRowInput> | null | undefined;
  places?: Array<AdminCatalogImportRowInput> | null | undefined;
  traits?: Array<AdminCatalogImportRowInput> | null | undefined;
};

export type AdminCatalogImportRowInput = {
  /** Ignored by morals and traits */
  artworkUrl?: string | null | undefined;
  /** Required for `characters` */
  category?: CharacterCategory | null | undefined;
  /** Required for `characters` */
  characterType?: CharacterType | null | undefined;
  /** Ignored by morals and traits */
  description?: string | null | undefined;
  /** Slug: lowercase latin letters, digits and dashes. Generated from the name if omitted */
  id?: string | null | undefined;
  /** Russian is required, English is optional */
  labels: LocalizedTextInput;
  /** `characters` only; ignored otherwise */
  promptDescription?: string | null | undefined;
  /** Defaults to the end of the list */
  sortOrder?: number | null | undefined;
};

export type AdminCatalogItemInput = {
  /** Ignored by morals and traits */
  artworkUrl?: string | null | undefined;
  /** Required for `characters` */
  category?: CharacterCategory | null | undefined;
  /** Required for `characters` */
  characterType?: CharacterType | null | undefined;
  /** Ignored by morals and traits */
  description?: string | null | undefined;
  /** Slug: lowercase latin letters, digits and dashes */
  id: string;
  /** Russian is required, English is optional */
  labels: LocalizedTextInput;
  /** `characters` only; ignored otherwise */
  promptDescription?: string | null | undefined;
  /** Defaults to the end of the list */
  sortOrder?: number | null | undefined;
};

export type AdminCreateUploadInput = {
  /** A catalogue table, or another admin-owned namespace such as cross-promo */
  collection: string;
  /** One of image/png, image/jpeg, image/webp, image/gif */
  contentType: string;
  /** Only used to pick the key’s extension */
  fileName?: string | null | undefined;
  /** The row’s id; same slug rule as the row itself */
  id: string;
};

export type AdminLoginInput = {
  email: string;
  /** At least 8 characters */
  password: string;
};

export type AdminSettingsInput = {
  openRouterModel?: string | null | undefined;
  storyPromptKind?: string | null | undefined;
  storyPromptScary?: string | null | undefined;
  termsEn?: string | null | undefined;
  termsRu?: string | null | undefined;
};

export type AdminTestModelInput = {
  /** OpenRouter model id */
  model: string;
  /** Sent as the single user message */
  prompt: string;
};

export type AdminUpdateUserInput = {
  plan?: Plan | null | undefined;
  role?: UserRole | null | undefined;
};

/** The identity provider a user signed in with */
export type AuthProvider = 'apple' | 'email' | 'facebook' | 'google';

/** Which catalogue table an admin catalogue operation addresses */
export type CatalogCollection = 'characters' | 'conflicts' | 'morals' | 'places' | 'traits';

/** Which shelf of the character picker a character belongs to */
export type CharacterCategory = 'animals' | 'other' | 'people';

/** The role a character plays in a story */
export type CharacterType = 'helper' | 'hero' | 'villain';

export type CrossPromoAppInput = {
  appStoreUrl?: string | null | undefined;
  artworkUrl?: string | null | undefined;
  googlePlayUrl?: string | null | undefined;
  name: string;
};

/** A language the API speaks; `ru` is the default and the fallback */
export type Locale = 'en' | 'ru';

export type LocalizedTextInput = {
  /** Up to 100 characters; empty or omitted clears the translation */
  en?: string | null | undefined;
  /** 1–100 characters */
  ru: string;
};

/** Subscription plan; `plus` unlocks the gated story settings */
export type Plan = 'free' | 'plus';

/** `self` — the wizard walked every step; `ai` — only a type and settings; `sequel` — a continuation of an existing story of the caller's own (TAL-97) */
export type StoryMode = 'ai' | 'self' | 'sequel';

/** Poll `story(id:)` until this is `ready` or `failed` */
export type StoryStatus = 'failed' | 'generating' | 'queued' | 'ready';

/** Who is holding the phone: a parent or a kid */
export type UserRole = 'kid' | 'parent';

export type AdminLoginMutationVariables = Exact<{
  input: AdminLoginInput;
}>;

export type AdminLoginMutation = {
  adminLogin: {
    accessToken: string;
    tokenType: string;
    expiresIn: string;
    admin: { id: string; email: string; createdAt: string; lastLoginAt: string | null };
  };
};

export type AdminMeQueryVariables = Exact<{ [key: string]: never }>;

export type AdminMeQuery = {
  adminMe: { id: string; email: string; createdAt: string; lastLoginAt: string | null };
};

export type AdminStatsQueryVariables = Exact<{ [key: string]: never }>;

export type AdminStatsQuery = {
  adminStats: {
    usersTotal: number;
    usersPlus: number;
    usersToday: number;
    usersWeek: number;
    storiesTotal: number;
    storiesToday: number;
    storiesWeek: number;
    storiesByStatus: Array<{ status: StoryStatus; count: number }>;
  };
};

export type AdminUsersQueryVariables = Exact<{
  search?: string | null | undefined;
  plan?: Plan | null | undefined;
  role?: UserRole | null | undefined;
  limit?: number | null | undefined;
  offset?: number | null | undefined;
}>;

export type AdminUsersQuery = {
  adminUsers: {
    total: number;
    items: Array<{
      id: string;
      email: string | null;
      providers: Array<AuthProvider>;
      role: UserRole;
      plan: Plan;
      locale: Locale;
      storiesCount: number;
      createdAt: string;
    }>;
  };
};

export type AdminUserQueryVariables = Exact<{
  id: string | number;
}>;

export type AdminUserQuery = {
  adminUser: {
    id: string;
    email: string | null;
    providers: Array<AuthProvider>;
    role: UserRole;
    plan: Plan;
    locale: Locale;
    storiesCount: number;
    createdAt: string;
  };
};

export type AdminUpdateUserMutationVariables = Exact<{
  id: string | number;
  input: AdminUpdateUserInput;
}>;

export type AdminUpdateUserMutation = {
  adminUpdateUser: {
    id: string;
    email: string | null;
    providers: Array<AuthProvider>;
    role: UserRole;
    plan: Plan;
    locale: Locale;
    storiesCount: number;
    createdAt: string;
  };
};

export type AdminStoriesQueryVariables = Exact<{
  status?: StoryStatus | null | undefined;
  userId?: string | number | null | undefined;
  mode?: StoryMode | null | undefined;
  limit?: number | null | undefined;
  offset?: number | null | undefined;
}>;

export type AdminStoriesQuery = {
  adminStories: {
    total: number;
    items: Array<{
      id: string;
      mode: StoryMode | null;
      status: StoryStatus;
      title: string | null;
      locale: Locale;
      createdAt: string;
      user: { id: string; email: string | null };
    }>;
  };
};

export type AdminStoryQueryVariables = Exact<{
  id: string | number;
}>;

export type AdminStoryQuery = {
  adminStory: {
    id: string;
    mode: StoryMode | null;
    status: StoryStatus;
    title: string | null;
    text: string | null;
    error: string | null;
    errorDetail: string | null;
    draft: unknown;
    locale: Locale;
    createdAt: string;
    updatedAt: string;
    user: {
      id: string;
      email: string | null;
      providers: Array<AuthProvider>;
      role: UserRole;
      plan: Plan;
    };
  };
};

export type AdminRetryStoryMutationVariables = Exact<{
  id: string | number;
}>;

export type AdminRetryStoryMutation = {
  adminRetryStory: {
    id: string;
    status: StoryStatus;
    error: string | null;
    errorDetail: string | null;
    updatedAt: string;
  };
};

export type AdminGenerateStoryMutationVariables = Exact<{
  id: string | number;
}>;

export type AdminGenerateStoryMutation = {
  adminGenerateStory: {
    id: string;
    status: StoryStatus;
    title: string | null;
    text: string | null;
    error: string | null;
    errorDetail: string | null;
    updatedAt: string;
  };
};

export type AdminCatalogQueryVariables = Exact<{
  collection: CatalogCollection;
}>;

export type AdminCatalogQuery = {
  adminCatalog: Array<{
    collection: CatalogCollection;
    id: string;
    category: CharacterCategory | null;
    characterType: CharacterType | null;
    artworkUrl: string | null;
    description: string | null;
    promptDescription: string | null;
    sortOrder: number;
    labels: { ru: string; en: string | null };
  }>;
};

export type AdminUpsertCatalogItemMutationVariables = Exact<{
  collection: CatalogCollection;
  input: AdminCatalogItemInput;
}>;

export type AdminUpsertCatalogItemMutation = {
  adminUpsertCatalogItem: {
    collection: CatalogCollection;
    id: string;
    category: CharacterCategory | null;
    characterType: CharacterType | null;
    artworkUrl: string | null;
    description: string | null;
    promptDescription: string | null;
    sortOrder: number;
    labels: { ru: string; en: string | null };
  };
};

export type AdminImportCatalogMutationVariables = Exact<{
  input: AdminCatalogImportInput;
}>;

export type AdminImportCatalogMutation = {
  adminImportCatalog: {
    characters: {
      created: number;
      updated: number;
      errors: Array<{ index: number; id: string | null; reason: string }>;
    };
    places: {
      created: number;
      updated: number;
      errors: Array<{ index: number; id: string | null; reason: string }>;
    };
    conflicts: {
      created: number;
      updated: number;
      errors: Array<{ index: number; id: string | null; reason: string }>;
    };
    morals: {
      created: number;
      updated: number;
      errors: Array<{ index: number; id: string | null; reason: string }>;
    };
    traits: {
      created: number;
      updated: number;
      errors: Array<{ index: number; id: string | null; reason: string }>;
    };
  };
};

export type AdminCreateUploadMutationVariables = Exact<{
  input: AdminCreateUploadInput;
}>;

export type AdminCreateUploadMutation = {
  adminCreateUpload: {
    url: string;
    fields: unknown;
    key: string;
    publicUrl: string;
    expiresAt: string;
    maxBytes: number;
  };
};

export type AdminDeleteUploadMutationVariables = Exact<{
  key: string;
}>;

export type AdminDeleteUploadMutation = { adminDeleteUpload: boolean };

export type AdminDeleteCatalogItemMutationVariables = Exact<{
  collection: CatalogCollection;
  id: string | number;
}>;

export type AdminDeleteCatalogItemMutation = { adminDeleteCatalogItem: boolean };

export type AdminReorderCatalogMutationVariables = Exact<{
  collection: CatalogCollection;
  ids: Array<string> | string;
}>;

export type AdminReorderCatalogMutation = {
  adminReorderCatalog: Array<{
    collection: CatalogCollection;
    id: string;
    category: CharacterCategory | null;
    characterType: CharacterType | null;
    artworkUrl: string | null;
    sortOrder: number;
    labels: { ru: string; en: string | null };
  }>;
};

export type AdminSettingsQueryVariables = Exact<{ [key: string]: never }>;

export type AdminSettingsQuery = {
  adminSettings: {
    storyPromptKind: string;
    storyPromptScary: string;
    defaultStoryPromptKind: string;
    defaultStoryPromptScary: string;
    promptVariables: Array<string>;
    termsRu: string | null;
    termsEn: string | null;
    openRouterModel: string;
    updatedAt: string | null;
  };
};

export type AdminUpdateSettingsMutationVariables = Exact<{
  input: AdminSettingsInput;
}>;

export type AdminUpdateSettingsMutation = {
  adminUpdateSettings: {
    storyPromptKind: string;
    storyPromptScary: string;
    defaultStoryPromptKind: string;
    defaultStoryPromptScary: string;
    promptVariables: Array<string>;
    termsRu: string | null;
    termsEn: string | null;
    openRouterModel: string;
    updatedAt: string | null;
  };
};

export type AdminOpenRouterModelsQueryVariables = Exact<{ [key: string]: never }>;

export type AdminOpenRouterModelsQuery = {
  adminOpenRouterModels: Array<{
    id: string;
    name: string;
    promptPrice: number | null;
    completionPrice: number | null;
    contextLength: number | null;
  }>;
};

export type AdminTestModelMutationVariables = Exact<{
  input: AdminTestModelInput;
}>;

export type AdminTestModelMutation = {
  adminTestModel: {
    model: string;
    text: string;
    promptTokens: number | null;
    completionTokens: number | null;
    totalTokens: number | null;
    cost: number | null;
    costEstimated: boolean;
    durationMs: number;
  };
};

export type AdminSupportMessagesQueryVariables = Exact<{
  limit?: number | null | undefined;
  offset?: number | null | undefined;
}>;

export type AdminSupportMessagesQuery = {
  adminSupportMessages: {
    total: number;
    items: Array<{
      id: string;
      text: string;
      createdAt: string;
      readAt: string | null;
      forwardedAt: string | null;
      user: { id: string; email: string | null };
    }>;
  };
};

export type AdminMarkSupportMessageReadMutationVariables = Exact<{
  id: string | number;
}>;

export type AdminMarkSupportMessageReadMutation = {
  adminMarkSupportMessageRead: { id: string; readAt: string | null };
};

export type AdminCrossPromoAppsQueryVariables = Exact<{ [key: string]: never }>;

export type AdminCrossPromoAppsQuery = {
  adminCrossPromoApps: Array<{
    id: string;
    name: string;
    artworkUrl: string | null;
    googlePlayUrl: string | null;
    appStoreUrl: string | null;
    sortOrder: number;
  }>;
};

export type AdminCreateCrossPromoAppMutationVariables = Exact<{
  input: CrossPromoAppInput;
}>;

export type AdminCreateCrossPromoAppMutation = {
  adminCreateCrossPromoApp: {
    id: string;
    name: string;
    artworkUrl: string | null;
    googlePlayUrl: string | null;
    appStoreUrl: string | null;
    sortOrder: number;
  };
};

export type AdminUpdateCrossPromoAppMutationVariables = Exact<{
  id: string | number;
  input: CrossPromoAppInput;
}>;

export type AdminUpdateCrossPromoAppMutation = {
  adminUpdateCrossPromoApp: {
    id: string;
    name: string;
    artworkUrl: string | null;
    googlePlayUrl: string | null;
    appStoreUrl: string | null;
    sortOrder: number;
  };
};

export type AdminDeleteCrossPromoAppMutationVariables = Exact<{
  id: string | number;
}>;

export type AdminDeleteCrossPromoAppMutation = { adminDeleteCrossPromoApp: boolean };

export type AdminReorderCrossPromoAppsMutationVariables = Exact<{
  ids: Array<string | number> | string | number;
}>;

export type AdminReorderCrossPromoAppsMutation = {
  adminReorderCrossPromoApps: Array<{
    id: string;
    name: string;
    artworkUrl: string | null;
    googlePlayUrl: string | null;
    appStoreUrl: string | null;
    sortOrder: number;
  }>;
};

export const AdminLoginDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminLogin' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminLoginInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminLogin' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'accessToken' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tokenType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'expiresIn' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'admin' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastLoginAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminLoginMutation, AdminLoginMutationVariables>;
export const AdminMeDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminMe' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminMe' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lastLoginAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminMeQuery, AdminMeQueryVariables>;
export const AdminStatsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminStats' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminStats' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'usersTotal' } },
                { kind: 'Field', name: { kind: 'Name', value: 'usersPlus' } },
                { kind: 'Field', name: { kind: 'Name', value: 'usersToday' } },
                { kind: 'Field', name: { kind: 'Name', value: 'usersWeek' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storiesTotal' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storiesToday' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storiesWeek' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'storiesByStatus' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'count' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminStatsQuery, AdminStatsQueryVariables>;
export const AdminUsersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminUsers' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'search' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'plan' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'role' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRole' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'offset' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminUsers' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'search' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'search' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'plan' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'plan' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'role' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'role' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'limit' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'offset' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'offset' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'providers' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'plan' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'locale' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'storiesCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminUsersQuery, AdminUsersQueryVariables>;
export const AdminUserDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminUser' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminUser' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'providers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'plan' } },
                { kind: 'Field', name: { kind: 'Name', value: 'locale' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storiesCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminUserQuery, AdminUserQueryVariables>;
export const AdminUpdateUserDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminUpdateUser' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminUpdateUserInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminUpdateUser' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                { kind: 'Field', name: { kind: 'Name', value: 'providers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                { kind: 'Field', name: { kind: 'Name', value: 'plan' } },
                { kind: 'Field', name: { kind: 'Name', value: 'locale' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storiesCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminUpdateUserMutation, AdminUpdateUserMutationVariables>;
export const AdminStoriesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminStories' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'status' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'StoryStatus' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'userId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'mode' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'StoryMode' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'offset' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminStories' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'status' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'status' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'userId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'userId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'mode' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'mode' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'limit' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'offset' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'offset' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'mode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'locale' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'user' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminStoriesQuery, AdminStoriesQueryVariables>;
export const AdminStoryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminStory' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminStory' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'mode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'error' } },
                { kind: 'Field', name: { kind: 'Name', value: 'errorDetail' } },
                { kind: 'Field', name: { kind: 'Name', value: 'draft' } },
                { kind: 'Field', name: { kind: 'Name', value: 'locale' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'user' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'providers' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'role' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'plan' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminStoryQuery, AdminStoryQueryVariables>;
export const AdminRetryStoryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminRetryStory' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminRetryStory' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'error' } },
                { kind: 'Field', name: { kind: 'Name', value: 'errorDetail' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminRetryStoryMutation, AdminRetryStoryMutationVariables>;
export const AdminGenerateStoryDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminGenerateStory' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminGenerateStory' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'error' } },
                { kind: 'Field', name: { kind: 'Name', value: 'errorDetail' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminGenerateStoryMutation, AdminGenerateStoryMutationVariables>;
export const AdminCatalogDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminCatalog' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CatalogCollection' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminCatalog' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'collection' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'collection' } },
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'labels' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'ru' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'en' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                { kind: 'Field', name: { kind: 'Name', value: 'characterType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                { kind: 'Field', name: { kind: 'Name', value: 'promptDescription' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminCatalogQuery, AdminCatalogQueryVariables>;
export const AdminUpsertCatalogItemDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminUpsertCatalogItem' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CatalogCollection' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminCatalogItemInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminUpsertCatalogItem' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'collection' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'collection' } },
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'labels' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'ru' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'en' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                { kind: 'Field', name: { kind: 'Name', value: 'characterType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                { kind: 'Field', name: { kind: 'Name', value: 'promptDescription' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminUpsertCatalogItemMutation,
  AdminUpsertCatalogItemMutationVariables
>;
export const AdminImportCatalogDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminImportCatalog' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminCatalogImportInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminImportCatalog' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'characters' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'created' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updated' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errors' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'index' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'reason' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'places' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'created' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updated' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errors' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'index' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'reason' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'conflicts' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'created' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updated' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errors' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'index' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'reason' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'morals' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'created' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updated' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errors' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'index' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'reason' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'traits' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'created' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updated' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errors' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'index' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'reason' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminImportCatalogMutation, AdminImportCatalogMutationVariables>;
export const AdminCreateUploadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminCreateUpload' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminCreateUploadInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminCreateUpload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fields' } },
                { kind: 'Field', name: { kind: 'Name', value: 'key' } },
                { kind: 'Field', name: { kind: 'Name', value: 'publicUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'expiresAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'maxBytes' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminCreateUploadMutation, AdminCreateUploadMutationVariables>;
export const AdminDeleteUploadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminDeleteUpload' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'key' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminDeleteUpload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'key' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'key' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminDeleteUploadMutation, AdminDeleteUploadMutationVariables>;
export const AdminDeleteCatalogItemDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminDeleteCatalogItem' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CatalogCollection' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminDeleteCatalogItem' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'collection' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminDeleteCatalogItemMutation,
  AdminDeleteCatalogItemMutationVariables
>;
export const AdminReorderCatalogDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminReorderCatalog' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CatalogCollection' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminReorderCatalog' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'collection' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'collection' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'collection' } },
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'labels' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'ru' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'en' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                { kind: 'Field', name: { kind: 'Name', value: 'characterType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminReorderCatalogMutation, AdminReorderCatalogMutationVariables>;
export const AdminSettingsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminSettings' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminSettings' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'storyPromptKind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storyPromptScary' } },
                { kind: 'Field', name: { kind: 'Name', value: 'defaultStoryPromptKind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'defaultStoryPromptScary' } },
                { kind: 'Field', name: { kind: 'Name', value: 'promptVariables' } },
                { kind: 'Field', name: { kind: 'Name', value: 'termsRu' } },
                { kind: 'Field', name: { kind: 'Name', value: 'termsEn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'openRouterModel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminSettingsQuery, AdminSettingsQueryVariables>;
export const AdminUpdateSettingsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminUpdateSettings' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminSettingsInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminUpdateSettings' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'storyPromptKind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'storyPromptScary' } },
                { kind: 'Field', name: { kind: 'Name', value: 'defaultStoryPromptKind' } },
                { kind: 'Field', name: { kind: 'Name', value: 'defaultStoryPromptScary' } },
                { kind: 'Field', name: { kind: 'Name', value: 'promptVariables' } },
                { kind: 'Field', name: { kind: 'Name', value: 'termsRu' } },
                { kind: 'Field', name: { kind: 'Name', value: 'termsEn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'openRouterModel' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminUpdateSettingsMutation, AdminUpdateSettingsMutationVariables>;
export const AdminOpenRouterModelsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminOpenRouterModels' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminOpenRouterModels' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'promptPrice' } },
                { kind: 'Field', name: { kind: 'Name', value: 'completionPrice' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contextLength' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminOpenRouterModelsQuery, AdminOpenRouterModelsQueryVariables>;
export const AdminTestModelDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminTestModel' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminTestModelInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminTestModel' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'model' } },
                { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                { kind: 'Field', name: { kind: 'Name', value: 'promptTokens' } },
                { kind: 'Field', name: { kind: 'Name', value: 'completionTokens' } },
                { kind: 'Field', name: { kind: 'Name', value: 'totalTokens' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cost' } },
                { kind: 'Field', name: { kind: 'Name', value: 'costEstimated' } },
                { kind: 'Field', name: { kind: 'Name', value: 'durationMs' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminTestModelMutation, AdminTestModelMutationVariables>;
export const AdminSupportMessagesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminSupportMessages' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'offset' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminSupportMessages' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'limit' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'limit' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'offset' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'offset' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'text' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'readAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'forwardedAt' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'user' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminSupportMessagesQuery, AdminSupportMessagesQueryVariables>;
export const AdminMarkSupportMessageReadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminMarkSupportMessageRead' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminMarkSupportMessageRead' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'readAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminMarkSupportMessageReadMutation,
  AdminMarkSupportMessageReadMutationVariables
>;
export const AdminCrossPromoAppsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AdminCrossPromoApps' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminCrossPromoApps' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'googlePlayUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'appStoreUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminCrossPromoAppsQuery, AdminCrossPromoAppsQueryVariables>;
export const AdminCreateCrossPromoAppDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminCreateCrossPromoApp' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CrossPromoAppInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminCreateCrossPromoApp' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'googlePlayUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'appStoreUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminCreateCrossPromoAppMutation,
  AdminCreateCrossPromoAppMutationVariables
>;
export const AdminUpdateCrossPromoAppDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminUpdateCrossPromoApp' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CrossPromoAppInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminUpdateCrossPromoApp' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'googlePlayUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'appStoreUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminUpdateCrossPromoAppMutation,
  AdminUpdateCrossPromoAppMutationVariables
>;
export const AdminDeleteCrossPromoAppDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminDeleteCrossPromoApp' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminDeleteCrossPromoApp' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminDeleteCrossPromoAppMutation,
  AdminDeleteCrossPromoAppMutationVariables
>;
export const AdminReorderCrossPromoAppsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'AdminReorderCrossPromoApps' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminReorderCrossPromoApps' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'artworkUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'googlePlayUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'appStoreUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sortOrder' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AdminReorderCrossPromoAppsMutation,
  AdminReorderCrossPromoAppsMutationVariables
>;
